/** Garage and crew management (§5). Every write here is owner-only. */

import { z } from 'zod';

import { garageDto, memberDto, membershipDto, userDto } from './entities.js';
import { role } from './enums.js';
import { shortText, uuid } from './primitives.js';

/** `GET /me` — the current user plus the garages they can see. */
export const meResponse = z.object({
  user: userDto,
  memberships: z.array(membershipDto),
});
export type MeResponse = z.infer<typeof meResponse>;

/**
 * `PATCH /me` — the caller's own reminder preferences.
 *
 * Both optional so a client can flip one without restating the other. There is
 * no user id on the wire: the caller is the resource, exactly as with
 * `DELETE /me`.
 */
export const updateMeRequest = z.object({
  remind60: z.boolean().optional(),
  remind30: z.boolean().optional(),
});
export type UpdateMeRequest = z.infer<typeof updateMeRequest>;

export const createGarageRequest = z.object({
  name: shortText(80),
});
export type CreateGarageRequest = z.infer<typeof createGarageRequest>;

export const updateGarageRequest = z.object({
  name: shortText(80).optional(),
  /**
   * The maintenance-detail toggle lives here, on the garage PATCH, rather than
   * on the public on/off endpoint — flipping it must NOT re-mint the public
   * token (which `POST /public` does), so an owner can turn part numbers on and
   * off without breaking the link they already shared.
   */
  publicShowMaintenance: z.boolean().optional(),
});
export type UpdateGarageRequest = z.infer<typeof updateGarageRequest>;

/**
 * `POST /garages/:id/public` — toggle link sharing.
 *
 * Turning it on mints a fresh token; turning it off revokes it. Re-enabling
 * therefore produces a *new* link, which is the point: revocation has to
 * actually revoke (§11, Sharing).
 */
export const setPublicRequest = z.object({
  isPublic: z.boolean(),
});
export type SetPublicRequest = z.infer<typeof setPublicRequest>;

export const setPublicResponse = z.object({
  isPublic: z.boolean(),
  publicToken: z.string().nullable(),
  /** The full shareable URL, assembled server-side so the client can't guess wrong. */
  publicUrl: z.string().nullable(),
});
export type SetPublicResponse = z.infer<typeof setPublicResponse>;

/**
 * `POST /garages/:id/members` — invite crew by email.
 *
 * Only `crew` is invitable: a garage has exactly one owner (§2), so accepting
 * `owner` here would be a privilege-escalation path. Co-owners are a v2 item.
 */
export const inviteMemberRequest = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: z.literal(role.enum.crew).default(role.enum.crew),
});
export type InviteMemberRequest = z.infer<typeof inviteMemberRequest>;

export const garageIdParam = z.object({ garageId: uuid });
export type GarageIdParam = z.infer<typeof garageIdParam>;

export const memberParam = z.object({ garageId: uuid, userId: uuid });
export type MemberParam = z.infer<typeof memberParam>;

export const garageResponse = garageDto;
export const garageListResponse = z.array(garageDto);
export const memberListResponse = z.array(memberDto);
